import { log } from '../../loader/essentials/logger.js';

/**
 * A typeguard to check if an given dom node is an dom element.
 *
 * @param x node to check
 * @returns true if node is an element
 */
const isElement = (x: Node): x is HTMLElement => x.nodeType === Node.ELEMENT_NODE;

/**
 * An function to be called if an element was added to the dom.
 */
export type ElementListener = (element: HTMLElement) => void;

/**
 * A data structure to store `ElementListener` callbacks per element id,
 * which should get notified when an element was added to the dom.
 */
const listenersPerId: Record<string, ElementListener[]> = {};

/**
 * A mutation observer to monitor changes to the dom.
 * If an element is added to the dom, all registered `ElementListener` callbacks will be notified.
 * The callback functions are deregistered once notified.
 */
const observer = new MutationObserver((mutations: MutationRecord[]) => {
    for (const { addedNodes } of mutations) {
        for (const addedNode of Array.from(addedNodes)) {
            if (isElement(addedNode) && addedNode.id) {
                const listeners = listenersPerId[addedNode.id] || [];
                while (listeners.length) {
                    const listener = listeners.shift()!;
                    try {
                        listener(addedNode);
                    } catch (e) {
                        log.error('error calling element listener', [listener]);
                    }
                }
            }
        }
    }
});

observer.observe(document.documentElement, {
    childList: true,
    attributes: false,
    characterData: false,
    subtree: true,
});

/**
 * Registers a `ElementListener` callback function for an given element id,
 * which gets notified if an matching element is added to the dom.
 *
 * @param id a given element id to watch
 * @param listener a callback function
 */
export const waitForElement = (id: string, listener: ElementListener): void => {
    listenersPerId[id] ||= [];
    listenersPerId[id].push(listener);
};

/**
 * Returns a `Promise` which gets resolved when an element with given id is registered.
 *
 * @param id a given element id to watch
 * @returns a promise of the element
 */
export const waitForElementPromise = async (id: string): Promise<HTMLElement> => new Promise((resolve) => waitForElement(id, resolve));
